import React from 'react';

import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import {default as settingsSelectors} from 'redux/settings/selectors';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {InputAdornment} from '@mui/material';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikNumberField from 'components/inputs/FormikNumberField';
import FormikRadioButton from 'components/inputs/FormikRadioButton';
import Panel from 'components/Panel';
import {layoutActions} from 'features/layout/redux';
import {Trans, useTranslation} from 'react-i18next';
import {useSelector, useDispatch} from 'react-redux';
import {State} from 'redux/reducer';
import {settingsActions} from 'redux/settings';
import {JobHistorySettings, IJobHistorySettings} from 'types/JobHistorySettings';
import {DialogTypes} from 'common/names';
import Dialog from 'components/Dialog';
import {useSendData} from 'features/layout/CommonPage/redux/hooks/useSendData';
import Formik from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';

type DialogProps = {
   ok: (values: IJobHistorySettings, callbacks: any) => any;
   cancel: () => void;
};

const Form = ({cancel}: Omit<DialogProps, 'ok'>) => {
   const {t} = useTranslation();
   const numJobHistories = useSelector<State, number>(
      (state) => state.layout.numJobHistories['numJobHistories'] || 0
   );
   const getValue = useFieldValueGetter<
      IJobHistorySettings & {jobHistoryDisplayByNumber: string}
   >();

   // const retainByRecords = getValue('retainByRecords');
   // const retainByAge = getValue('retainByAge');
   const jobHistoryDisplayByNumber = getValue('jobHistoryDisplayByNumber');
   const maxNumberRecords = getValue('maxNumberRecords');
   const maxAge = getValue('maxAge');
   const timestamp = getValue('timestamp');

   return (
      <>
         <Panel>
            <Typography>
               <Trans>Retain job history records for display by job monitor by number of:</Trans>
            </Typography>
            <Grid container spacing={1} alignItems="center">
               <Grid item xs={6}>
                  <FormikRadioButton
                     autoFocus
                     name="jobHistoryDisplayByNumber"
                     value="retainByRecords"
                     label={t('Jobs per Transaction')}
                  />
               </Grid>
               <Grid item xs={6}>
                  <FormikNumberField
                     name="maxNumberRecords"
                     disabled={jobHistoryDisplayByNumber !== 'retainByRecords'}
                     value={
                        jobHistoryDisplayByNumber !== 'retainByRecords' ? null : maxNumberRecords
                     }
                  />
               </Grid>
               <Grid item xs={6}>
                  <FormikRadioButton
                     name="jobHistoryDisplayByNumber"
                     value="retainByAge"
                     label={t('Days')}
                  />
               </Grid>
               <Grid item xs={6}>
                  <FormikNumberField
                     name="maxAge"
                     disabled={jobHistoryDisplayByNumber !== 'retainByAge'}
                     value={jobHistoryDisplayByNumber !== 'retainByAge' ? null : maxAge}
                  />
               </Grid>
               <Grid item xs={6}>
                  <Typography>
                     <Trans>Total Job History Records Available: </Trans>
                  </Typography>
               </Grid>
               <Grid item xs={6}>
                  <Typography>{numJobHistories}</Typography>
               </Grid>
               <Grid item xs={6}>
                  <FormikCheckbox
                     label={t('Display Detailed File Status')}
                     name="detailedJobHistory"
                  />
               </Grid>
               <Grid item xs={12}>
                  <FormikNumberField
                     label={t('Job Monitor Update Rate')}
                     name="clientUpdateRate"
                     InputProps={{
                        endAdornment: (
                           <InputAdornment position="end">
                              <Trans>seconds</Trans>
                           </InputAdornment>
                        ),
                     }}
                  />
               </Grid>
               <Grid item xs={12}>
                  <LastUpdatedLabel date={timestamp} />
               </Grid>
            </Grid>
         </Panel>
         <Box textAlign="center">
            <FormFooterButtons cancel={cancel} />
         </Box>
      </>
   );
};

const SettingsJobMonitorForm = ({ok, cancel}: DialogProps) => {
   const settings = useSelector(settingsSelectors.getJobHistorySettings);

   const dispatch = useDispatch();
   React.useEffect(() => {
      dispatch(settingsActions.getJobHistorySettings());
      dispatch(layoutActions.getNumJobHistories());
   }, [dispatch]);

   return (
      <Formik
         initialValues={{
            ...settings,
            jobHistoryDisplayByNumber: settings.retainByAge ? 'retainByAge' : 'retainByRecords',
         }}
         onSubmit={(values, callbacks) => {
            if (values.jobHistoryDisplayByNumber === 'retainByRecords') {
               values.retainByRecords = true;
               values.retainByAge = false;
            } else if (values.jobHistoryDisplayByNumber === 'retainByAge') {
               values.retainByAge = true;
               values.retainByRecords = false;
            }
            const {jobHistoryDisplayByNumber, ...restValues} = values;
            return ok(restValues, callbacks);
         }}
         validationSchema={JobHistorySettings}
         enableReinitialize
      >
         <Form cancel={cancel} />
      </Formik>
   );
};

type Props = {
   onClose: () => void;
};
export const SettingsJobMonitorDialog = ({onClose}: Props) => {
   const sendData = useSendData();
   return (
      <Dialog
         maxWidth="sm"
         header={DialogTypes.SettingsJobMonitorDialog.dialogName}
         close={onClose}
      >
         <SettingsJobMonitorForm
            ok={(values, callbacks) => {
               sendData(DialogTypes.SettingsJobMonitorDialog.id, values, callbacks);
               onClose();
            }}
            cancel={onClose}
         />
      </Dialog>
   );
};
export default React.memo(SettingsJobMonitorDialog);
